import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { getApiBaseUrl } from '../utils/apiAssetUrl';

function readLogin() {
    try {
        const raw = localStorage.getItem('login');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const pid = parsed.permanent_ID;
        if (pid === undefined || pid === null || String(pid).trim() === '') return null;
        return parsed;
    } catch {
        return null;
    }
}

/**
 * After an admin deletes a user, JWT/localStorage may still look "logged in".
 * On each navigation, confirm the user row still exists; if not, clear storage and send to login.
 */
function SessionValidator() {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const toastShownRef = useRef(false);
    const abortRef = useRef(null);

    useEffect(() => {
        const login = readLogin();
        if (!login) {
            toastShownRef.current = false;
            return;
        }

        const base = getApiBaseUrl();
        if (!base) return;

        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;

        (async () => {
            try {
                const res = await fetch(
                    `${base}/register/session-alive/${encodeURIComponent(String(login.permanent_ID).trim())}`,
                    { method: 'GET', signal: ac.signal }
                );
                if (res.status === 404) {
                    localStorage.removeItem('login');
                    localStorage.removeItem('token');
                    if (!toastShownRef.current) {
                        toastShownRef.current = true;
                        toast({
                            description:
                                'Your account is no longer available. You have been signed out.',
                            status: 'warning',
                            duration: 6000,
                            position: 'top-center',
                            isClosable: true,
                        });
                    }
                    navigate('/login', { replace: true });
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
            }
        })();

        return () => ac.abort();
    }, [location.pathname, navigate, toast]);

    return null;
}

export default SessionValidator;
